import styled, { keyframes, css } from 'styled-components';
import { BadgeSize } from '../../types/BadgeSize';

const shimmer = keyframes`
  0%   { transform: translateX(-120%) skewX(-20deg); opacity: 0; }
  20%  { opacity: 1; }
  80%  { opacity: 1; }
  100% { transform: translateX(220%) skewX(-20deg); opacity: 0; }
`;

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50%       { opacity: 1;   transform: scale(1.06); }
`;

const appear = keyframes`
  from {
    opacity: 0;
    transform: scale(0.82) translateY(4px);
    filter: blur(4px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
    filter: blur(0);
  }
`;

const sizeStyles: Record<BadgeSize, ReturnType<typeof css>> = {
  'extra-small': css`
    font-size: 0.55rem;
    padding: 0.28em 0.65em;
    letter-spacing: 0.09em;
    border-radius: 5px;
  `,
  small: css`
    font-size: 0.7rem;
    padding: 0.35em 0.78em;
    letter-spacing: 0.1em;
    border-radius: 6px;
  `,
  medium: css`
    font-size: 0.85rem;
    padding: 0.45em 1em;
    letter-spacing: 0.12em;
    border-radius: 8px;
  `,
  large: css`
    font-size: 1.05rem;
    padding: 0.52em 1.2em;
    letter-spacing: 0.13em;
    border-radius: 10px;
  `,
  'extra-large': css`
    font-size: 1.3rem;
    padding: 0.6em 1.5em;
    letter-spacing: 0.15em;
    border-radius: 12px;
  `,
};

export const GlowRing = styled.span<{ baseColor: string }>`
  position: absolute;
  inset: -2px;
  border-radius: inherit;
  background: transparent;
  border: 1.5px solid ${({ baseColor }) => baseColor}88;
  animation: ${pulseGlow} 2.8s ease-in-out infinite;
  pointer-events: none;
  z-index: 1;
`;

export const ShimmerLayer = styled.span`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  overflow: hidden;
  pointer-events: none;
  z-index: 2;

  &::after {
    content: '';
    position: absolute;
    top: -20%;
    left: 0;
    width: 40%;
    height: 140%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.18) 40%,
      rgba(255, 255, 255, 0.38) 50%,
      rgba(255, 255, 255, 0.18) 60%,
      transparent 100%
    );
    animation: ${shimmer} 3.5s ease-in-out infinite;
    animation-delay: 0.8s;
  }
`;

export const StyledBadge = styled.span<{
  background: string;
  baseColor: string;
  size: BadgeSize;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  background: ${({ background }) => background};
  color: #fff;
  font-family: 'SF Pro Display', 'Segoe UI', system-ui, sans-serif;
  font-weight: 700;
  text-align: center;
  cursor: default;
  user-select: none;
  white-space: nowrap;

  /* Subtle top-edge glass highlight */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 8%;
    right: 8%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.65) 50%,
      transparent
    );
    border-radius: 50%;
    z-index: 3;
    pointer-events: none;
  }

  /* Colored drop-shadow for depth */
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.18),
    0 3px 7px ${({ baseColor }) => baseColor}55,
    0 6px 18px ${({ baseColor }) => baseColor}33,
    inset 0 1px 0 rgba(255, 255, 255, 0.22), inset 0 -1px 0 rgba(0, 0, 0, 0.15);

  animation: ${appear} 0.38s cubic-bezier(0.22, 1, 0.36, 1) both;

  transition: transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.22s ease, filter 0.22s ease;

  &:hover {
    transform: translateY(-2px) scale(1.04);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2),
      0 6px 16px ${({ baseColor }) => baseColor}66,
      0 12px 30px ${({ baseColor }) => baseColor}44,
      inset 0 1px 0 rgba(255, 255, 255, 0.28),
      inset 0 -1px 0 rgba(0, 0, 0, 0.15);
    filter: brightness(1.08);
  }

  &:active {
    transform: translateY(0) scale(0.97);
    transition-duration: 0.08s;
  }

  .badge-label {
    position: relative;
    z-index: 4;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25),
      0 0 12px rgba(255, 255, 255, 0.15);
  }

  ${({ size }) => sizeStyles[size] ?? sizeStyles.medium}
`;
